/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDisclosure } from "@jumbo/components/JumboDialog/hooks/useDisclosure";
import DialogConfirm from "@jumbo/components/JumboDialog/components/DialogConfirm";

import Stack from "@mui/material/Stack";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Card, CardContent } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

import { usePagination } from "app/hooks/usePagination";
import { Table } from "app/components";
import Div from "@jumbo/shared/Div";
import { deleteUser, getUserList, toggleAlwaysAllow, toggleAutoAssignLead, toggleIsActive } from "app/redux/slice/userSlice";
import { getLoginSettings, updateLoginSettings } from "app/redux/slice/settingsSlice";
import { useDispatch, useSelector } from "react-redux";
import { markModuleRead, fetchUnreadCount } from "app/redux/slice/notificationSlice";
import { useSnackbar } from "notistack";
import Loader from "@jumbo/components/Loader";
import ClearIcon from "@mui/icons-material/Clear";
import { userType } from "app/helpers/userType";
import { userEditById, USERS_ADD } from "app/routes/constant";
import { getRoleList } from "app/redux/slice/roleSlice";
import JumboAutoComplete from "@jumbo/components/JumboAutoComplete";

const fmt = (h) => `${h % 12 || 12}:00 ${h < 12 ? "AM" : "PM"}`;
const hours = Array.from({ length: 24 }, (_, i) => i);

const User = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { isMA } = userType();

  const userData = useSelector((state) => state?.user);
  const settingsData = useSelector((state) => state?.settings);
  const [searchText, setSearchText] = useState("");
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("createdAt");
  const [roleId, setRoleId] = useState();
  const [type, setType] = useState("All");
  const [autoAssignLead, setAutoAssignLead] = useState("All");
  const roleData = useSelector((state) => state?.role);

  // Login hours edit dialog state
  const [hoursDialogOpen, setHoursDialogOpen] = useState(false);
  const [startHour, setStartHour] = useState(10);
  const [endHour, setEndHour] = useState(19);

  const {
    isOpen: isDelete,
    onClose: closeDelete,
    actionProps: deleteActionProps,
    stageAction: deleteStageAction,
  } = useDisclosure();
  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    usePagination({ id: "user" });
  const [permissionData] = useState(
    JSON.parse(localStorage.getItem("permission"))
  );

  const [permission, setPermission] = useState({
    createPermission: false,
    updatePermission: false,
    deletePermission: false,
  });
  const getPermission = () => {
    if (isMA) {
      setPermission({
        createPermission: true,
        updatePermission: true,
        deletePermission: true,
      });
    } else {
      let permissionValue = {
        createPermission: false,
        updatePermission: false,
        deletePermission: false,
      };
      const rolePermission = permissionData?.filter((item) => {
        return item?.moduleName?.includes("User") && item;
      });
      rolePermission?.length &&
        rolePermission.forEach(function (num) {
          if (num?.name === "create-user") {
            permissionValue.createPermission = true;
          }
          if (num?.name === "delete-user") {
            permissionValue.deletePermission = true;
          }
          if (num?.name === "update-user") {
            permissionValue.updatePermission = true;
          }
        });
      setPermission(permissionValue);
    }
  };

  useEffect(() => {
    getPermission();
  }, []);

  useEffect(() => {
    if (isMA) {
      dispatch(getLoginSettings());
    }
  }, []);

  // Sync dialog state when settings load
  useEffect(() => {
    setStartHour(settingsData.loginStartHour);
    setEndHour(settingsData.loginEndHour);
  }, [settingsData.loginStartHour, settingsData.loginEndHour]);

  const handleToggleIsActive = async (row) => {
    const res = await dispatch(
      toggleIsActive({ id: row._id, isActive: !row.isActive })
    ).unwrap();
    if (res?.status === 200) {
      getData();
    } else {
      enqueueSnackbar(res?.data?.responseMessage || "Update failed", { variant: "error" });
    }
  };

  const handleToggleAlwaysAllow = async (row) => {
    const res = await dispatch(
      toggleAlwaysAllow({ id: row._id, alwaysAllow: !row.alwaysAllow })
    ).unwrap();
    if (res?.status === 200) {
      getData();
    } else {
      enqueueSnackbar(res?.data?.responseMessage || "Update failed", { variant: "error" });
    }
  };

  const handleToggleAutoAssignLead = async (row) => {
    const res = await dispatch(
      toggleAutoAssignLead({ id: row._id, autoAssignLead: !row.autoAssignLead })
    ).unwrap();
    if (res?.status === 200) {
      getData();
    } else {
      enqueueSnackbar(res?.data?.responseMessage || "Update failed", { variant: "error" });
    }
  };

  const handleSaveHours = async () => {
    if (startHour >= endHour) {
      enqueueSnackbar("Start time must be before end time", { variant: "error" });
      return;
    }
    const res = await dispatch(updateLoginSettings({ loginStartHour: startHour, loginEndHour: endHour })).unwrap();
    if (res?.status === 200) {
      dispatch(getLoginSettings());
      enqueueSnackbar("Login hours updated", { variant: "success" });
      setHoursDialogOpen(false);
    } else {
      enqueueSnackbar(res?.data?.responseMessage || "Update failed", { variant: "error" });
    }
  };

  const columns = [
    { id: "name", label: "Name", sort: true },
    {
      id: "role.name",
      label: "Role",
      sort: true,
      render: (row) => row?.role?.name,
    },
    { id: "email", label: "Email", sort: true },
    { id: "mobileNumber", label: "Mobile Number", sort: true, minWidth: 145 },
    ...(isMA
      ? [
          {
            id: "alwaysAllow",
            label: "24 X 7 Login",
            align: "center",
            minWidth: 100,
            render: (row) => (
              <Tooltip title={row.alwaysAllow ? "Disable 24/7 login" : "Enable 24/7 login"}>
                <Switch
                  checked={!!row.alwaysAllow}
                  onChange={() => handleToggleAlwaysAllow(row)}
                  color="primary"
                  size="small"
                />
              </Tooltip>
            ),
          },
          {
            id: "autoAssignLead",
            label: "Auto Assign Lead",
            align: "center",
            minWidth: 130,
            render: (row) => (
              <Tooltip
                title={
                  row.autoAssignLead
                    ? "Stop giving this user leads automatically"
                    : "Include this user in the lead rotation"
                }
              >
                <Switch
                  checked={!!row.autoAssignLead}
                  onChange={() => handleToggleAutoAssignLead(row)}
                  color="primary"
                  size="small"
                />
              </Tooltip>
            ),
          },
          {
            id: "isActive",
            label: "Active",
            align: "center",
            minWidth: 100,
            render: (row) => (
              <Tooltip title={row.isActive !== false ? "Deactivate user" : "Activate user"}>
                <Switch
                  checked={row.isActive !== false}
                  onChange={() => handleToggleIsActive(row)}
                  color="success"
                  size="small"
                />
              </Tooltip>
            ),
          },
        ]
      : []),
    {
      id: "actions",
      label: "Actions",
      align: "right",
      minWidth: 100,
      render: (row) => (
        <>
          <IconButton
            disabled={!permission.updatePermission}
            onClick={() => {
              navigate(userEditById(row._id));
            }}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            disabled={!permission.deletePermission}
            onClick={() => {
              deleteStageAction(row);
            }}
          >
            <DeleteIcon color={"error"} />
          </IconButton>
        </>
      ),
    },
  ];

  const DeleteUser = () => {
    dispatch(deleteUser(deleteActionProps?._id))
      .unwrap()
      .then((res) => {
        if (res?.status === 200) {
          enqueueSnackbar(res.data?.responseMessage, {
            variant: "success",
          });
          getData();
          closeDelete();
        } else {
          enqueueSnackbar(res.data?.responseMessage, {
            variant: "error",
          });
        }
      });
  };

  const getData = () => {
    localStorage.setItem("user-search", searchText.trim());
    const value = {
      page: page,
      perPage: rowsPerPage,
      search: searchText.trim(),
      ...(roleId && { roleId: roleId._id }),
      ...(type !== "All" && { type }),
      ...(autoAssignLead !== "All" && {
        autoAssignLead: autoAssignLead === "On",
      }),
      ...(orderBy && { sort: order === "asc" ? 1 : -1, field: orderBy }),
    };
    dispatch(getUserList(value));
    dispatch(markModuleRead("User")).then(() => {
      dispatch(fetchUnreadCount("User"));
    });
  };

  useEffect(() => {
    dispatch(getRoleList());
  }, []);

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, dispatch, order, orderBy]);

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleChangePage(0, 0);
      getData();
    }
  };
  const sortData = (Order, OrderBy) => {
    setOrder(Order);
    setOrderBy(OrderBy);
  };
  return (
    <>
      {userData?.loading ? (
        <Loader />
      ) : (
        <>
          <Card sx={{ display: "flex", mb: 3 }}>
            <Div sx={{ display: "flex", flexDirection: "column", flex: "1" }}>
              <CardContent>
                <Grid container spacing={{ xs: 2, md: 3 }}>
                  <Grid item xs={12} sm={isMA ? 3 : 5}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Search"
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      onKeyPress={handleKeyPress}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment
                            position="end"
                            style={{
                              display: searchText === "" ? "none" : "flex",
                            }}
                            onClick={() => setSearchText("")}
                          >
                            <ClearIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <JumboAutoComplete
                      options={roleData?.list || []}
                      label="Role"
                      size="small"
                      getOptionLabel={(option) => option?.name}
                      name={`roleId`}
                      value={roleId}
                      onChange={(evt, value) => setRoleId(value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <FormControl fullWidth>
                      <InputLabel>Type</InputLabel>
                      <Select
                        name="type"
                        id="type"
                        value={type}
                        label="type"
                        size="small"
                        onChange={(e) => setType(e.target.value)}
                      >
                        <MenuItem value={"All"}>{"All"}</MenuItem>
                        {["Office", "Mill"].map((val) => {
                          return <MenuItem value={val}>{val}</MenuItem>;
                        })}
                      </Select>
                    </FormControl>
                  </Grid>
                  {isMA && (
                    <Grid item xs={12} sm={2}>
                      <FormControl fullWidth>
                        <InputLabel>Auto Assign Lead</InputLabel>
                        <Select
                          name="autoAssignLead"
                          id="autoAssignLead"
                          value={autoAssignLead}
                          label="Auto Assign Lead"
                          size="small"
                          onChange={(e) => setAutoAssignLead(e.target.value)}
                        >
                          <MenuItem value={"All"}>All</MenuItem>
                          <MenuItem value={"On"}>On</MenuItem>
                          <MenuItem value={"Off"}>Off</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                  <Grid item xs={12} sm={2}>
                    <Button
                      fullWidth
                      type="submit"
                      variant="contained"
                      onClick={() => {
                        handleChangePage(0, 0);
                        getData();
                      }}
                    >
                      Submit
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Div>
          </Card>
          <Card>
            <CardContent>
              <Stack
                justifyContent="space-between"
                alignItems="center"
                direction={{ xs: "column", sm: "row" }}
                sx={{ mb: 3 }}
              >
                <Typography variant={"h2"} mb={0}>
                  Users List
                </Typography>
                <Stack direction="row" alignItems="center" spacing={2}>
                  {isMA && (
                    <Tooltip title="Edit login hours">
                      <Button
                        variant="outlined"
                        startIcon={<AccessTimeIcon />}
                        onClick={() => setHoursDialogOpen(true)}
                        size="small"
                        color="secondary"
                      >
                        {fmt(settingsData.loginStartHour)} – {fmt(settingsData.loginEndHour)}
                      </Button>
                    </Tooltip>
                  )}
                  {permission.createPermission && (
                    <LoadingButton
                      onClick={() => navigate(USERS_ADD)}
                      sx={{ mt: { xs: 1, md: 0 } }}
                      type="submit"
                      variant="contained"
                    >
                      Add user
                    </LoadingButton>
                  )}
                </Stack>
              </Stack>
              <Table
                columns={columns}
                rows={userData?.list}
                page={page}
                rowsPerPage={rowsPerPage}
                count={userData?.total}
                Order={order}
                OrderBy={orderBy}
                sortData={sortData}
                handleChangePage={handleChangePage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
              />
            </CardContent>
          </Card>

          {/* Login Hours Edit Dialog */}
          <Dialog open={hoursDialogOpen} onClose={() => setHoursDialogOpen(false)} maxWidth="xs" fullWidth>
            <DialogTitle>Edit Login Hours</DialogTitle>
            <DialogContent>
              <Stack spacing={3} sx={{ mt: 1 }}>
                <FormControl fullWidth>
                  <InputLabel>Login Start Time</InputLabel>
                  <Select
                    value={startHour}
                    label="Login Start Time"
                    onChange={(e) => setStartHour(e.target.value)}
                  >
                    {hours.map((h) => (
                      <MenuItem key={h} value={h}>{fmt(h)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Login End Time</InputLabel>
                  <Select
                    value={endHour}
                    label="Login End Time"
                    onChange={(e) => setEndHour(e.target.value)}
                  >
                    {hours.map((h) => (
                      <MenuItem key={h} value={h}>{fmt(h)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button variant="outlined" color="secondary" onClick={() => setHoursDialogOpen(false)}>
                Cancel
              </Button>
              <Button variant="contained" onClick={handleSaveHours}>
                Save
              </Button>
            </DialogActions>
          </Dialog>

          <DialogConfirm
            isOpen={isDelete}
            onClose={closeDelete}
            title="Are you sure you want to delete this user?"
            onConfirm={DeleteUser}
            btnLabels={{ cancel: "Cancel", confirm: "Delete" }}
          />
        </>
      )}
    </>
  );
};

export default User;
